import concat from "lodash/concat";
import { useApiFilters, useSyncFiltersV2Meta } from "PFApp/components/filters_v2";
import { hasDefaultValue, isValueEmpty } from "PFCore/helpers/filters";
import { getMatchDisplayValue } from "PFCore/helpers/match_value";
import { useMatchesInfinite } from "PFCore/hooks/queries/matches/use_matches";
import { KnownFiltersV2 } from "PFCore/types/filters_v2";
import { Meta, OrderMeta, OrderOption, Profile, QueryParams } from "PFTypes";
import { useEffect, useMemo, useState } from "react";

const PER_PAGE = 10;

const formatScore = (score: number | null | undefined) => getMatchDisplayValue(score, { digits: 0 });

export type MatchesTableEntry = {
  profile: Profile;
  matchScore: string | undefined;
  availabilityScore: string | undefined;
};

export type MatchesResponse = {
  matchesMeta: Meta;
  matchesData: MatchesTableEntry[];
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  lastRefreshAt: string | null;
};

type MatchesOrder = {
  orderMeta: OrderMeta | undefined;
  handleChangeOrder: (value: OrderOption) => void;
};

type UseReassignMatches = {
  sourceProfileIds: number[];
  activityId: number;
};

type UseReassignMatchesReturn = {
  matchesResponse: MatchesResponse;
  matchesQueryParams: QueryParams;
  matchesOrder: MatchesOrder;
};

export const useReassignMatches = ({
  sourceProfileIds,
  activityId
}: UseReassignMatches): UseReassignMatchesReturn => {
  const [matchesQueryParamsWithoutFilters, setMatchesQueryParamsWithoutFilters] = useState<
    Omit<QueryParams, "filters">
  >({
    perPage: PER_PAGE
  });
  const { apiFilters } = useApiFilters({ sendDefaultsAsUndefined: false, sendEmptyAsUndefined: false });

  const matchesQueryParams = useMemo(
    () => ({
      ...matchesQueryParamsWithoutFilters,
      filters: apiFilters
    }),
    [matchesQueryParamsWithoutFilters, apiFilters]
  );

  const updateMatchesParams = (params: QueryParams) =>
    setMatchesQueryParamsWithoutFilters((currentParams) => ({
      ...currentParams,
      ...params
    }));

  const {
    data: matchesPaginatedData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useMatchesInfinite(activityId, matchesQueryParams, { gcTime: 0 });

  const mergedPages = concat(...(matchesPaginatedData?.pages.map(({ entries }) => entries) || []));

  const meta = useMemo(
    () =>
      matchesPaginatedData && matchesPaginatedData.pages.length > 0
        ? matchesPaginatedData?.pages[0].meta
        : ({} as Meta),
    [matchesPaginatedData]
  );

  const syncFiltersV2 = useSyncFiltersV2Meta();
  useEffect(() => {
    const availabilityFilter = meta?.filtersV2?.mainFilters?.[KnownFiltersV2.Availability];
    if (availabilityFilter && hasDefaultValue(availabilityFilter) && isValueEmpty(availabilityFilter.value)) {
      // eslint-disable-next-line react-hooks/immutability
      availabilityFilter.value = availabilityFilter.defaultValue!;
    }
    syncFiltersV2(meta);
  }, [meta]);

  const lastRefreshAt =
    meta && meta.timestamps && "matched_at" in meta.timestamps ? meta.timestamps.matched_at : null;

  const matchesData = mergedPages
    .filter(({ profile }) => !sourceProfileIds.includes(profile.id))
    .map(({ profile, scores }) => ({
      profile,
      matchScore: formatScore(scores.normalizedScore),
      availabilityScore: formatScore(scores.availabilityScore)
    }));

  const orderMeta = meta?.order;

  const handleChangeOrder = (value: OrderOption) => updateMatchesParams({ order: value });

  const matchesResponse = {
    matchesMeta: meta,
    matchesData,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetching,
    isFetchingNextPage,
    lastRefreshAt
  };

  const matchesOrder = {
    orderMeta,
    handleChangeOrder
  };

  return {
    matchesResponse,
    matchesQueryParams,
    matchesOrder
  };
};
